'use client';

import { useSyncExternalStore } from 'react';
export type ThemeMode = 'light' | 'dark';
const getServerThemeMode = (): ThemeMode => 'dark';

/**
 * Detect theme mode from DOM attributes or system preference.
 * Checks: data-theme attribute > .light/.dark class > prefers-color-scheme media query
 */
function detectThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return getServerThemeMode();
  const html = document.documentElement;

  // Explicit attribute takes priority
  const dataTheme = html.getAttribute('data-theme');
  if (dataTheme === 'light') return 'light';
  if (dataTheme === 'dark') return 'dark';

  // Explicit class (set by ThemeContextProvider)
  if (html.classList.contains('light')) return 'light';
  if (html.classList.contains('dark')) return 'dark';

  // Fallback to system preference (covers "system" mode where no class is applied)
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
function subscribeToThemeMode(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {};
  const html = document.documentElement;
  const observer = new MutationObserver(onStoreChange);
  observer.observe(html, {
    attributes: true,
    attributeFilter: ['class', 'data-theme']
  });
  const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
  mediaQuery.addEventListener('change', onStoreChange);
  return () => {
    observer.disconnect();
    mediaQuery.removeEventListener('change', onStoreChange);
  };
}

/**
 * Hook to detect the current theme mode from the document.
 * Looks for data-theme attribute, dark/light class, or falls back to system preference.
 */
export function useThemeMode() {
  return useSyncExternalStore(subscribeToThemeMode, detectThemeMode, getServerThemeMode);
}